import { createBrowserClient } from "@supabase/ssr";
import { getBrowserTenantId } from "./browser-tenant";
import { authCookieKey, type SessionArea, sessionArea } from "./fetch-timeout";

let cachedClient: ReturnType<typeof createBrowserClient> | null = null;
let cachedOrigin = "";
let cachedTenantId = "";
let cachedArea: SessionArea | "" = "";

/**
 * Fallback source for the tenant id when a browser-side query fires before
 * TenantProvider's useEffect has run. The root server layout renders
 * `<meta name="x-tenant-id" content="...">`, which is available synchronously
 * at module init, so we never leave RLS without a tenant.
 */
function readTenantIdFromMeta(): string {
  if (typeof document === "undefined") return "";
  const el = document.querySelector('meta[name="x-tenant-id"]');
  return el?.getAttribute("content") ?? "";
}

/** Which session (admin or storefront) and cookie the browser client uses on
 * this page. Must match the server, middleware and proxy clients. */
export function browserSession(location: { host: string; pathname: string }): {
  area: SessionArea;
  cookieName: string;
} {
  const area = sessionArea(location.pathname);
  return { area, cookieName: authCookieKey(location.host, area) };
}

/**
 * Browser Supabase client — uses the current domain as the Supabase URL.
 * Includes x-tenant-id header for RLS.
 *
 * Singleton per origin + tenant.
 */
export function createClient() {
  if (typeof window === "undefined") {
    return createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
    );
  }

  const origin = window.location.origin;
  const tenantId = getBrowserTenantId() || cachedTenantId || readTenantIdFromMeta();
  if (tenantId && tenantId !== cachedTenantId) {
    cachedTenantId = tenantId;
    cachedClient = null;
  }

  // The admin and the storefront keep separate sessions (see sessionArea).
  const { area, cookieName } = browserSession(window.location);

  if (cachedClient && cachedOrigin === origin && cachedTenantId && cachedArea === area) {
    return cachedClient;
  }

  cachedOrigin = origin;
  cachedArea = area;

  // Always use origin — requests go through the reverse proxy (Caddy in prod,
  // Next.js rewrites in dev). This avoids CORS and keeps cookie names consistent.
  cachedClient = createBrowserClient(
    origin,
    process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
    {
      cookieOptions: { name: cookieName },
      // @supabase/ssr otherwise returns one process-wide client and ignores the
      // cookie name on later calls, so moving from the storefront to the admin
      // in the same tab would keep the storefront session. This module caches.
      isSingleton: false,
      global: {
        headers: cachedTenantId ? { "x-tenant-id": cachedTenantId } : {},
      },
    },
  );
  return cachedClient;
}
